/**
 * Pinch-to-zoom for a single lightbox slide.
 *
 * Replaces native browser page zoom inside the lightbox. Native zoom re-rasters
 * viewport-sized layers and escalates image decode resolution, which is one of the
 * ways this gallery OOM-killed mobile renderers. Here the zoom is purely a CSS
 * transform on the slide's already-loaded image and is capped at MAX_SCALE, so
 * zooming costs no additional image memory.
 *
 * Gestures (mobile layout only — the desktop gallery is a vertical scroll list):
 *   - two fingers: pinch to zoom, anchored on the midpoint between them
 *   - one finger while zoomed: pan, clamped to the image edges
 *   - double tap: toggle between 1x and DOUBLE_TAP_SCALE
 *
 * At 1x the transform is removed entirely so the element composites like any other
 * slide and the gallery keeps its horizontal swipe (see the `touch-action` note in
 * `apply`). `x-lightbox` calls `reset()` when a slide stops being the active one.
 */

const MIN_SCALE = 1
const MAX_SCALE = 3
const DOUBLE_TAP_SCALE = 2
const DOUBLE_TAP_WINDOW_MS = 300

// A pinch or pan ends with the finger lifting, which the browser also reports as a
// click. The lightbox closes on image click, so swallow clicks for a moment after
// any gesture to stop a zoom from dismissing the gallery.
const CLICK_SUPPRESS_MS = 400

// Matches the `lg-max:` breakpoint, where the gallery is a horizontal carousel of
// full-screen slides. Above it the layout is a vertical stack browsed with a mouse.
const MOBILE_LAYOUT_QUERY = '(max-width: 1023px)'

export class PinchZoomElement extends HTMLElement {
  private image: HTMLImageElement | null = null
  private scale = MIN_SCALE
  private x = 0
  private y = 0

  private isPinching = false
  private isPanning = false
  private startDistance = 0
  private startScale = MIN_SCALE
  private startX = 0
  private startY = 0
  private panOriginX = 0
  private panOriginY = 0
  private lastTapAt = 0
  private suppressClicksUntil = 0

  // Geometry of this container, captured once per gesture. `clampTranslation` and
  // `midpointFromCentre` both read layout, and `apply` writes a transform between them,
  // so reading per `touchmove` forced a synchronous layout on every event of a pinch or
  // pan — on mobile, inside the lightbox. Only the *image* transform changes during a
  // gesture; this element's own box does not, so one measurement per gesture is enough.
  private gestureRect: DOMRect | null = null

  // Allocated once. `isMobileLayout` is the first statement of both touch handlers, and
  // building a fresh MediaQueryList on every touch event is pure waste.
  private mobileLayoutQuery: MediaQueryList | null = null

  connectedCallback(): void {
    this.image = this.querySelector<HTMLImageElement>('img')

    this.addEventListener('touchstart', this.handleTouchStart, { passive: false })
    this.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.addEventListener('touchend', this.handleTouchEnd)
    this.addEventListener('touchcancel', this.handleTouchEnd)
    this.addEventListener('click', this.handleClickCapture, true)
  }

  disconnectedCallback(): void {
    this.removeEventListener('touchstart', this.handleTouchStart)
    this.removeEventListener('touchmove', this.handleTouchMove)
    this.removeEventListener('touchend', this.handleTouchEnd)
    this.removeEventListener('touchcancel', this.handleTouchEnd)
    this.removeEventListener('click', this.handleClickCapture, true)

    this.reset()
  }

  /** Returns the slide to 1x. Called by x-lightbox when the slide is scrolled away. */
  reset = (): void => {
    this.scale = MIN_SCALE
    this.x = 0
    this.y = 0
    this.apply()
  }

  private get isMobileLayout(): boolean {
    if (!this.mobileLayoutQuery) {
      this.mobileLayoutQuery = window.matchMedia(MOBILE_LAYOUT_QUERY)
    }
    return this.mobileLayoutQuery.matches
  }

  private get isZoomed(): boolean {
    return this.scale > MIN_SCALE
  }

  private apply(): void {
    if (!this.image) return

    if (!this.isZoomed) {
      // Drop the transform rather than writing scale(1): this releases the
      // compositor layer and restores the slide's `touch-action` from the
      // stylesheet (`pan-x`), which is what lets the gallery swipe again.
      this.image.style.transform = ''
      this.style.touchAction = ''
      return
    }

    this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`
    this.style.touchAction = 'none'
  }

  /** Measures this element's box once per gesture — see `gestureRect`. */
  private captureGestureRect(): DOMRect {
    if (!this.gestureRect) this.gestureRect = this.getBoundingClientRect()
    return this.gestureRect
  }

  /** Stops the image being panned away from the edges of its container. */
  private clampTranslation(): void {
    const rect = this.captureGestureRect()
    const maxX = (rect.width * (this.scale - 1)) / 2
    const maxY = (rect.height * (this.scale - 1)) / 2

    this.x = Math.min(maxX, Math.max(-maxX, this.x))
    this.y = Math.min(maxY, Math.max(-maxY, this.y))
  }

  private distanceBetween(a: Touch, b: Touch): number {
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
  }

  /** Midpoint of two touches, expressed relative to this element's centre. */
  private midpointFromCentre(a: Touch, b: Touch): { x: number; y: number } {
    const rect = this.captureGestureRect()

    return {
      x: (a.clientX + b.clientX) / 2 - (rect.left + rect.width / 2),
      y: (a.clientY + b.clientY) / 2 - (rect.top + rect.height / 2)
    }
  }

  private suppressClicks(): void {
    this.suppressClicksUntil = Date.now() + CLICK_SUPPRESS_MS
  }

  private handleDoubleTap(): void {
    const now = Date.now()
    const isDoubleTap = now - this.lastTapAt < DOUBLE_TAP_WINDOW_MS
    this.lastTapAt = now

    if (!isDoubleTap) return
    this.suppressClicks()

    if (this.isZoomed) {
      this.reset()
      return
    }

    this.scale = DOUBLE_TAP_SCALE
    this.x = 0
    this.y = 0
    this.apply()
  }

  private handleTouchStart = (e: TouchEvent): void => {
    // New gesture: drop the cached geometry so the first read below measures fresh
    // (the slide may have been resized or rotated since the last gesture).
    this.gestureRect = null

    if (!this.isMobileLayout) return

    if (e.touches.length === 2) {
      e.preventDefault()
      this.isPinching = true
      this.isPanning = false
      this.startDistance = this.distanceBetween(e.touches[0], e.touches[1])
      this.startScale = this.scale
      this.startX = this.x
      this.startY = this.y
      return
    }

    if (e.touches.length !== 1) return
    this.handleDoubleTap()

    if (!this.isZoomed) return
    this.isPanning = true
    this.panOriginX = e.touches[0].clientX
    this.panOriginY = e.touches[0].clientY
    this.startX = this.x
    this.startY = this.y
  }

  private handleTouchMove = (e: TouchEvent): void => {
    if (!this.isMobileLayout) return

    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault()
      if (this.startDistance === 0) return

      const distance = this.distanceBetween(e.touches[0], e.touches[1])
      const next = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, (this.startScale * distance) / this.startDistance)
      )
      const midpoint = this.midpointFromCentre(e.touches[0], e.touches[1])
      const ratio = next / this.startScale

      // Keep whatever sits under the fingers pinned there as the scale changes.
      this.x = midpoint.x * (1 - ratio) + this.startX * ratio
      this.y = midpoint.y * (1 - ratio) + this.startY * ratio
      this.scale = next

      this.clampTranslation()
      this.apply()
      this.suppressClicks()
      return
    }

    if (this.isPanning && e.touches.length === 1 && this.isZoomed) {
      e.preventDefault()
      this.x = this.startX + (e.touches[0].clientX - this.panOriginX)
      this.y = this.startY + (e.touches[0].clientY - this.panOriginY)

      this.clampTranslation()
      this.apply()
      this.suppressClicks()
    }
  }

  private handleTouchEnd = (e: TouchEvent): void => {
    if (e.touches.length > 0) return

    this.isPinching = false
    this.isPanning = false
    this.gestureRect = null

    // Settle back to exactly 1x so the gallery reliably regains the swipe gesture
    // instead of being left at something like 1.02x.
    if (this.scale <= MIN_SCALE) this.reset()
  }

  /**
   * The lightbox closes when the image is clicked, which on touch would dismiss the
   * gallery on the first tap of a double tap and make zooming unreachable. Swallow
   * image clicks for the whole mobile layout: the close button is always on screen.
   * Desktop has no zoom, so click-to-close is left intact there.
   */
  private handleClickCapture = (e: Event): void => {
    if (!this.isMobileLayout && !this.isZoomed && Date.now() >= this.suppressClicksUntil) return

    // Runs in the capture phase, ahead of the lightbox's close-on-image-click.
    e.stopPropagation()
    e.preventDefault()
  }
}
